import { playSample } from './audio'
import { TIMER_TICK_SECONDS } from './constants'
import { BLOCK_SPAWN_SFX, WHISTLE_SFX } from './missionSfx'
import { concealServerStorageTemplate } from './primitives/serverStorageTemplate'
import { createDeathWatcher } from './primitives/watchAvatarDeath'
import {
  findStateByName,
  showHudText,
  spawnProgrammedClone,
} from './sharedHelpers'
import {
  BehaviorContext,
  BehaviorHandler,
  MissionBehaviors,
  HudStyle,
} from './types'

/**
 * RJF10（M10 サバイバル）のミッション環境ビヘイビア。
 *
 * テンプレート rbxl（src/missions/templates/RJF10.rbxl）のギミックを移植する:
 * - ServerScriptService/CloneScript: プレイ開始 10 秒後にタイマーを開始し、
 *   以降 3 秒ごとに ①あかいブロック / ②きいろいブロック のどちらかを
 *   ランダムにクローンして配置する（ブロックの動きは学習者のプログラム）。
 * - StarterGui/LocalScript: タイマーを毎秒 +1 で表示。ゲームオーバーで停止。
 * - Base/Fall/FallScript: 落下受け（巨大な板）に触れたらゲームオーバー
 *   （Roll The Dice 音 + タイマー停止）。
 * - ServerScriptService/GameOverScript: 死亡（Humanoid.Died）でも同じ
 *   ゲームオーバー処理。
 *
 * プレビューでの近似・意図的な差異:
 * - タイマー・ゲームオーバー表示は HUD テキストで再現する。
 * - ブロック生成時に生成音（コンッ）、ゲームオーバー時にホイッスル音を
 *   実音源で鳴らす。
 * - 死亡検知は Humanoid.Died の代わりに体力 0 の毎フレーム監視で行う。
 */

/** タイマー・クローン開始までの待機秒数（Lua: wait(10)）。 */
const SETUP_DELAY_SECONDS = 10

/** クローンの生成間隔（Lua: wait(3)）。 */
const CLONE_INTERVAL_SECONDS = 3

/** クローン元テンプレートと落下受けのオブジェクト名。 */
const RED_TEMPLATE_NAME = '①あかいブロック'
const YELLOW_TEMPLATE_NAME = '②きいろいブロック'
const FALL_NAME = 'Fall'

/** タイマー GUI の実機デザイン（ScreenGui/TextLabel の実測値）。 */
const TIMER_HUD_STYLE: HudStyle = {
  background: '#FFFFFF',
  color: '#000000',
  border: '#000000',
  borderWidth: 5,
  left: 10,
  top: 10,
}

export const createRjf10Behaviors = (): MissionBehaviors => {
  // Lua: ReplicatedStorage.isGameOver。Fall 接触と死亡の両経路で立つ。
  let isGameOver = false

  /** ゲームオーバー処理（Lua: FallScript / GameOverScript の gameOver）。 */
  const gameOver: BehaviorHandler = (ctx) => {
    if (isGameOver) {
      return
    }
    isGameOver = true
    playSample(WHISTLE_SFX.url, WHISTLE_SFX.volume, ctx.signal)
  }

  /** タイマー表示（Lua: StarterGui/LocalScript）。ゲームオーバーで止まる。 */
  const runTimer = async (ctx: BehaviorContext): Promise<void> => {
    let timer = 0
    while (!isGameOver && !ctx.signal.aborted) {
      ctx.setHudText?.(String(timer), TIMER_HUD_STYLE)
      timer += 1
      await ctx.wait(TIMER_TICK_SECONDS)
    }
  }

  /** 死亡監視（Lua: GameOverScript の Humanoid.Died）。 */
  const watchDeath = createDeathWatcher({
    once: true,
    // Fall 接触経路で isGameOver が立ったら死亡前でも監視を終える（旧 while 条件の再現）。
    shouldContinue: () => !isGameOver,
    onDeath: gameOver,
  })

  /**
   * ミッション本体（CloneScript）。10 秒後にタイマー開始 + 3 秒ごとに
   * 赤/黄のブロックをランダムにクローンする。
   */
  const runMission: BehaviorHandler = async (ctx) => {
    const { world } = ctx
    // クローン元はループ前に一度だけ束縛する（Lua: local plus/minus =
    // ServerStorage[...]）。クローンはテンプレートと同名になるため、ループ内で
    // 名前検索すると学習者プログラムが動かしたクローンを元として拾いうる。
    const redTemplate = findStateByName(world, RED_TEMPLATE_NAME)
    const yellowTemplate = findStateByName(world, YELLOW_TEMPLATE_NAME)
    // 実機のテンプレートは ServerStorage にあり Workspace には存在しない。
    // シーンに置いてある代役の本体はプレイ中見えない・当たらないようにする
    // （表示・衝突は次プレイの world.reset() で復元される。RJF05 と同じ流儀）。
    for (const template of [redTemplate, yellowTemplate]) {
      if (template) {
        concealServerStorageTemplate(world, template.id)
      }
    }
    await ctx.wait(SETUP_DELAY_SECONDS)
    if (ctx.signal.aborted) {
      return
    }
    const loops = [runTimer(ctx), watchDeath(ctx)]
    // Lua の CloneScript は while true（ゲームオーバー後もクローン生成は続く）
    // なので、isGameOver では止めない。
    while (!ctx.signal.aborted) {
      // Lua: rand:NextInteger(1,2) で赤/黄を等確率に選ぶ。
      const template = Math.random() < 0.5 ? redTemplate : yellowTemplate
      if (template) {
        // Lua: Clone() は配下の Script ごと複製され即実行される。学習者が
        // テンプレート名に付けたプログラムをクローンでも起動する。
        spawnProgrammedClone(ctx, template, 'rjf10_block')
        playSample(BLOCK_SPAWN_SFX.url, BLOCK_SPAWN_SFX.volume, ctx.signal)
      }
      await ctx.wait(CLONE_INTERVAL_SECONDS)
    }
    // 停止（abort）時、タイマー・死亡監視の終了を待ってから抜ける。
    await Promise.all(loops)
  }

  const onTouchCharacter = new Map<string, BehaviorHandler>()
  // Lua: FallScript。落下受けに触れたらゲームオーバー。
  onTouchCharacter.set(FALL_NAME, gameOver)

  // 実機の TextLabel は初期 Text=0 がプレイ開始時から出ている
  // （タイマーループの起動は準備待ちの後）。
  const showInitialTimer: BehaviorHandler = showHudText('0', TIMER_HUD_STYLE)

  return {
    onPlay: [runMission, showInitialTimer],
    onTouchCharacter,
    // 実機ではテンプレートは ServerStorage にあり、本体の Script は実行されない。
    // テンプレート本体では学習者プログラムを起動しない（起動すると実行直後に
    // 本体が動き出し、クローンも動いた後の位置から生成されてしまう）。
    serverStorageTemplates: [RED_TEMPLATE_NAME, YELLOW_TEMPLATE_NAME],
  }
}
